/**
 * The product's number, date and recency formats (D-11).
 *
 * Four date formats and a "1436" beside a "1,436" on the same screen were the
 * single most-repeated finding across the audits. There is one function for
 * each now, and everything that prints a count or a day calls it.
 *
 * Dates are rendered in en-GB order without the comma ("16 Sep 2026") because
 * that reads the same to a VA reader and a European one, and because the
 * month is spelled: 09/10/2026 is ambiguous, "9 Oct 2026" is not.
 */

/**
 * The short month, spelled by us rather than by the runtime.
 *
 * `Intl` with `month: 'short'` on en-GB returns "Sept" for September on CLDR 42
 * and later, and "Sep" before it — so the same build printed a different date on
 * two machines. The product's date format is a product decision, not a locale
 * one: three letters, always.
 */
const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const shortDay = (d: Date) =>
  `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;

const LONG_DAY = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const NUMBER = new Intl.NumberFormat('en-GB');

export type DateStyle = 'short' | 'long' | 'time';

/**
 * `formatDate('2026-09-16T08:12:00Z')` → `16 Sep 2026`
 * `formatDate(iso, 'long')`            → `16 September 2026`
 * `formatDate(iso, 'time')`            → `16 Sep 2026, 08:12`
 *
 * An unparseable or missing value returns an empty string rather than
 * "Invalid Date": a blank cell is honest, a broken one is not.
 */
export function formatDate(
  iso: string | number | Date | null | undefined,
  style: DateStyle = 'short',
): string {
  if (iso == null || iso === '') return '';
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  if (style === 'long') return LONG_DAY.format(date);
  if (style === 'time') return `${shortDay(date)}, ${TIME.format(date)}`;
  return shortDay(date);
}

/**
 * WHY: one recency function, so the same timestamp reads the same on every
 * page. `formatDate` prints a day, right for events with a date (a signature,
 * a run). A list read for recency asks "how long ago", and a day answers that
 * only once the change is old enough that the day IS the answer. Past a week,
 * "9 d ago" is a subtraction the reader has to do.
 */
export function relativeTime(
  value: string | number | Date | null | undefined,
  now: number = Date.now(),
): string {
  if (value == null || value === '') return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.round((now - then) / 1000);
  // A clock skew between two machines should not print "in 4 min".
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return formatDate(value);
}

/**
 * Thousands separators everywhere a count is shown, including the rail counts:
 * "1,436" is read at a glance, "1436" is counted digit by digit.
 */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  return NUMBER.format(value);
}
